import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Select from "react-select";
import Layout from '../../Component/Layout';
import axios from 'axios';
import { Link, useLocation } from 'react-router-dom';
import { PURCHASE_API, MASTER_API } from "../../apiConfig";

const VENDOR_API = PURCHASE_API.replace("/purchase-request", ""); 

// Formats a date string (e.g. "2026-06-27" or an ISO timestamp) as dd-mm-yyyy for display.
const formatDateDMY = (value) => {
  if (!value) return "";
  const isoPart = String(value).slice(0, 10); // "yyyy-mm-dd"
  const [y, m, d] = isoPart.split("-");
  if (!y || !m || !d) return String(value);
  return `${d}-${m}-${y}`;
};

const rsCompact = {
  control: (base) => ({
    ...base,
    minHeight: "32px",
    height: "32px",
    fontSize: "13px",
    borderColor: "#ced4da",
    boxShadow: "none",
    "&:hover": { borderColor: "#86b7fe" },
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px" }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  indicatorsContainer: (base) => ({ ...base, height: "32px" }),
  menu: (base) => ({ ...base, zIndex: 99999, fontSize: "13px", position: "absolute" }),
};

// Smaller variant used only in the filter card (Product Type row, cable
// attribute grid, and the Test Type..To row) so all filter fields line up
// at the same compact size, kept separate from rsCompact so nothing else
// on the page (modal fields, etc.) is affected.
const rsFilterSmall = {
  ...rsCompact,
  control: (base) => ({
    ...rsCompact.control(base),
    height: "30px",
    minHeight: "30px",
    fontSize: "12px",
  }),
  valueContainer: (base) => ({ ...base, padding: "0 6px" }),
  indicatorsContainer: (base) => ({ ...base, height: "30px" }),
  menu: (base) => ({ ...base, zIndex: 99999, fontSize: "12px", position: "absolute" }),
};

function VendorReturn() {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("active");

  // Filter states
  const [selectedProductType, setSelectedProductType] = useState(null);
  const [selectedTestType, setSelectedTestType] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [selectedProductCode, setSelectedProductCode] = useState(null);
  const [description, setDescription] = useState("");
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // ── Cable attribute filters (mirrors Purchase Requisition / Purchase
  // Order / Purchase Info's 2nd filter row: Construction, Code, Cable Type,
  // Category, Cores, Color, Cross Section, Std. Ref, Voltage) ──
  const [selectedConstruction, setSelectedConstruction] = useState(null);
  const [selectedCode, setSelectedCode] = useState(null);
  const [selectedCableType, setSelectedCableType] = useState(null);
  const [selectedCableCategory, setSelectedCableCategory] = useState(null);
  const [selectedCores, setSelectedCores] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedCrossSection, setSelectedCrossSection] = useState(null);
  const [selectedStdRef, setSelectedStdRef] = useState(null);
  const [selectedVoltage, setSelectedVoltage] = useState(null);
  // Shared product master list (Construction..Voltage) — same endpoint the
  // other Purchase modules use, so these filter options are computed from
  // it client-side instead of needing separate backend endpoints per field.
  const [cableAttrProducts, setCableAttrProducts] = useState([]);

  // Dropdown options
  const [productTypeOptions, setProductTypeOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);
  const [productCodeOptions, setProductCodeOptions] = useState([]);
  const [batchOptions, setBatchOptions] = useState([]);

  // Table & counts
  const [tableData, setTableData] = useState([]);
  const [activeCount, setActiveCount] = useState(0);
  const [returnedCount, setReturnedCount] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(false);

  // Return modal: opened by the Return button, shows one comment box per
  // selected product instead of a single comment for the whole batch.
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [modalComments, setModalComments] = useState({}); // { [id]: comment }
  const [submitting, setSubmitting] = useState(false);

  // ── Fetch dropdown options on mount ────────────────────────────────────────
  useEffect(() => {
    axios.get(`${MASTER_API}/configuration/product-types`)
      .then(res => setProductTypeOptions((res.data || []).map(i => ({ value: i.name, label: i.name }))))
      .catch(err => console.error("Product Types Error:", err));

    axios.get(`${MASTER_API}/configuration/category`)
      .then(res => setCategoryOptions((res.data || []).map(i => ({ value: i.name, label: i.name }))))
      .catch(err => console.error("Category Error:", err));

    axios.get(`${MASTER_API}/configuration/sub-category`)
      .then(res => setSubCategoryOptions((res.data || []).map(i => ({ value: i.name, label: i.name }))))
      .catch(err => console.error("Sub Category Error:", err));

    axios.get(`${PURCHASE_API}/purchase-request/product-codes`)
      .then(res => {
        if (res.data.success) {
          setProductCodeOptions(res.data.data.map(i => ({
            label: i.productDescription ? `${i.productCode} - ${i.productDescription}` : i.productCode,
            value: i.productCode,
          })));
        }
      })
      .catch(err => console.error("Product Codes Error:", err));

    axios.get(`${VENDOR_API}/vendor-return/batches`)
      .then(res => { if (res.data.success) setBatchOptions(res.data.data); })
      .catch(err => console.error("Batches Error:", err));

    // ── Shared product master list (Construction..Voltage) — same endpoint
    // Purchase Requisition / Purchase Order / Purchase Info use, so the
    // cable-attribute filter options below are computed from it client-side
    // instead of needing separate endpoints. ──
    axios.get(`${MASTER_API}/bom/products`)
      .then(res => setCableAttrProducts(res.data.data || []))
      .catch(err => console.error("Failed loading products for cable attribute filters:", err.response?.data || err.message));

    fetchCounts();
  }, []); //eslint-disable-line

  // ── Cable attribute filter options (Construction..Voltage), computed off
  // the shared cableAttrProducts list — same pattern as Purchase
  // Requisition/Order/Info. ──
  const getCableAttrOptions = useCallback((key) => {
    const uniqueVals = Array.from(
      new Set(
        cableAttrProducts
          .map(p => p[key])
          .filter(v => v !== undefined && v !== null && v !== "")
          .map(v => String(v).trim())
          .filter(v => v !== "")
      )
    );
    return uniqueVals
      .map(v => ({ value: v, label: v }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [cableAttrProducts]);

  const constructionOptions = useMemo(() => getCableAttrOptions("construction"), [getCableAttrOptions]);
  const codeOptions = useMemo(() => getCableAttrOptions("code"), [getCableAttrOptions]);
  const cableTypeOptions = useMemo(() => getCableAttrOptions("cableType"), [getCableAttrOptions]);
  const cableCategoryOptions = useMemo(() => getCableAttrOptions("category"), [getCableAttrOptions]);
  const coresOptions = useMemo(() => getCableAttrOptions("noOfCores"), [getCableAttrOptions]);
  const colorOptions = useMemo(() => getCableAttrOptions("color"), [getCableAttrOptions]);
  const crossSectionOptions = useMemo(() => getCableAttrOptions("nominalCrossSection"), [getCableAttrOptions]);
  const stdRefOptions = useMemo(() => getCableAttrOptions("referenceOfStandard"), [getCableAttrOptions]);
  const voltageOptions = useMemo(() => getCableAttrOptions("voltage"), [getCableAttrOptions]);

  // ── Fetch counts ────────────────────────────────────────────────────────────
  const fetchCounts = () => {
    axios.get(`${VENDOR_API}/vendor-return/counts`)
      .then(res => {
        if (res.data.success) {
          setActiveCount(res.data.data.active_count || 0);
          setReturnedCount(res.data.data.returned_count || 0);
        }
      })
      .catch(err => console.error("Counts Error:", err));
  };

  // ── Fetch table data ────────────────────────────────────────────────────────
  const fetchListing = useCallback(() => {
    setLoading(true);
    setSelectedIds([]);
    const params = {
      tab: activeTab,
      ...(selectedProductType && { productType: selectedProductType.value }),
      ...(selectedCategory && { category: selectedCategory.value }),
      ...(selectedSubCategory && { subCategory: selectedSubCategory.value }),
      ...(selectedProductCode && { productCode: selectedProductCode.value }),
      ...(description && { description }),
      ...(selectedBatch && { batch: selectedBatch.value }),
      ...(fromDate && { fromDate }),
      ...(toDate && { toDate }),
      // ── Cable attribute filters (Construction..Voltage) ──
      ...(selectedConstruction && { construction: selectedConstruction.value }),
      ...(selectedCode && { code: selectedCode.value }),
      ...(selectedCableType && { cableType: selectedCableType.value }),
      ...(selectedCableCategory && { cableCategory: selectedCableCategory.value }),
      ...(selectedCores && { noOfCores: selectedCores.value }),
      ...(selectedColor && { color: selectedColor.value }),
      ...(selectedCrossSection && { nominalCrossSection: selectedCrossSection.value }),
      ...(selectedStdRef && { referenceOfStandard: selectedStdRef.value }),
      ...(selectedVoltage && { voltage: selectedVoltage.value }),
    };
    axios.get(`${VENDOR_API}/vendor-return/listing`, { params })
      .then(res => { if (res.data.success) setTableData(res.data.data); })
      .catch(err => console.error("Listing Error:", err))
      .finally(() => setLoading(false));
  }, [
    activeTab, selectedProductType, selectedCategory, selectedSubCategory, selectedProductCode,
    description, selectedBatch, fromDate, toDate,
    selectedConstruction, selectedCode, selectedCableType, selectedCableCategory,
    selectedCores, selectedColor, selectedCrossSection, selectedStdRef, selectedVoltage,
  ]);

  // Re-fetch whenever tab or any filter changes
  useEffect(() => { fetchListing(); }, [fetchListing]);

  // ── Client-side date-range filter ───────────────────────────────────────────
  // Defensive filter: the listing API is expected to honor fromDate/toDate, but in
  // case it doesn't (e.g. backend filter not applied), we still enforce the
  // selected date range here so the table never shows rows outside it.
  const filteredTableData = useMemo(() => {
    if (!fromDate && !toDate) return tableData;
    return tableData.filter((row) => {
      if (!row.grn_date) return false;
      const grnDate = String(row.grn_date).slice(0, 10); // normalize to yyyy-mm-dd
      if (fromDate && grnDate < fromDate) return false;
      if (toDate && grnDate > toDate) return false;
      return true;
    });
  }, [tableData, fromDate, toDate]);

  // ── Select all / single checkbox ────────────────────────────────────────────
  const allChecked = filteredTableData.length > 0 && selectedIds.length === filteredTableData.length;
  const toggleAll = () => setSelectedIds(allChecked ? [] : filteredTableData.map(r => r.id));
  const toggleOne = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  // ── Open the printable vendor return slip (Returned tab only) ───────────────
  // The slip is scoped by batch_number, since no separate vendor-return
  // number is generated. All three actions open the same form in a new tab;
  // `mode` tells VendorReturnForm whether to just display it (preview) or
  // auto-trigger the browser print dialog (download / print). There's no
  // separate binary file to download since the slip is rendered HTML, so
  // "Download" uses the browser's Print -> Save as PDF flow, same as Print.
  const openVendorReturnForm = (batchNumber, mode) => {
    if (!batchNumber) return;
    const base = `/cable/vendor-return/print/${encodeURIComponent(batchNumber)}`;
    const url = mode ? `${base}?mode=${mode}` : base;
    window.open(url, "_blank");
  };

  const handlePreview = (batchNumber) => openVendorReturnForm(batchNumber);
  const handleDownload = (batchNumber) => openVendorReturnForm(batchNumber, "download");
  const handlePrint = (batchNumber) => openVendorReturnForm(batchNumber, "print");

  // ── Return button handler ───────────────────────────────────────────────────
  const handleReturn = () => {
    if (selectedIds.length === 0) return alert("Please select at least one record.");
    // Seed the modal with an empty comment for each selected row, then let
    // the user fill them in and confirm from there.
    setModalComments(
      selectedIds.reduce((acc, id) => ({ ...acc, [id]: modalComments[id] || "" }), {})
    );
    setShowReturnModal(true);
  };

  const closeReturnModal = () => {
    if (submitting) return;
    setShowReturnModal(false);
  };

  const confirmReturn = () => {
    const items = selectedIds.map((id) => ({ id, comment: modalComments[id] || "" }));
    setSubmitting(true);
    axios.post(`${VENDOR_API}/vendor-return/initiate-return`, { items })
      .then(res => {
        if (res.data.success) {
          setShowReturnModal(false);
          setModalComments({});
          fetchListing();
          fetchCounts();
        } else {
          alert(res.data.message || "Return failed.");
        }
      })
      .catch(err => console.error("Return Error:", err))
      .finally(() => setSubmitting(false));
  };

  return (
    <Layout>
      <div style={{ padding: "24px 28px" }}>

        {/* ── Page Header ── */}
        <div style={styles.pageTitleBar}>
          <div>
            <div style={styles.pageTitle}>Vendor Return</div>
            <div style={styles.pageSubtitle}>Manage and track vendor return transactions</div>
          </div>
        </div>

        {/* ── Filter Card ── */}
        <div style={styles.filterCard}>
          <div className="row g-2">
            <div className="col-md-2">
              <label style={styles.filterLabel}>Product Type</label>
              <Select options={productTypeOptions} value={selectedProductType} onChange={setSelectedProductType} placeholder="Select" isClearable styles={rsFilterSmall} />
            </div>
          </div>

          {/* ── 2nd row: Cable attribute filters — matches Purchase Requisition / Purchase Order / Purchase Info layout ── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(9, minmax(80px, 1fr))", gap: 8, marginTop: 10 }}>
            <div>
              <label style={styles.filterLabel}>Construction</label>
              <Select options={constructionOptions} value={selectedConstruction} onChange={setSelectedConstruction} isClearable placeholder="Construction" styles={rsFilterSmall} menuPortalTarget={document.body} menuPosition="fixed" />
            </div>
            <div>
              <label style={styles.filterLabel}>Code</label>
              <Select options={codeOptions} value={selectedCode} onChange={setSelectedCode} isClearable placeholder="Code" styles={rsFilterSmall} menuPortalTarget={document.body} menuPosition="fixed" />
            </div>
            <div>
              <label style={styles.filterLabel}>Cable Type</label>
              <Select options={cableTypeOptions} value={selectedCableType} onChange={setSelectedCableType} isClearable placeholder="Cable Type" styles={rsFilterSmall} menuPortalTarget={document.body} menuPosition="fixed" />
            </div>
            <div>
              <label style={styles.filterLabel}>Category</label>
              <Select options={cableCategoryOptions} value={selectedCableCategory} onChange={setSelectedCableCategory} isClearable placeholder="Category" styles={rsFilterSmall} menuPortalTarget={document.body} menuPosition="fixed" />
            </div>
            <div>
              <label style={styles.filterLabel}>Cores</label>
              <Select options={coresOptions} value={selectedCores} onChange={setSelectedCores} isClearable placeholder="Cores" styles={rsFilterSmall} menuPortalTarget={document.body} menuPosition="fixed" />
            </div>
            <div>
              <label style={styles.filterLabel}>Color</label>
              <Select options={colorOptions} value={selectedColor} onChange={setSelectedColor} isClearable placeholder="Color" styles={rsFilterSmall} menuPortalTarget={document.body} menuPosition="fixed" />
            </div>
            <div>
              <label style={styles.filterLabel}>Cross Section</label>
              <Select options={crossSectionOptions} value={selectedCrossSection} onChange={setSelectedCrossSection} isClearable placeholder="Cross Section" styles={rsFilterSmall} menuPortalTarget={document.body} menuPosition="fixed" />
            </div>
            <div>
              <label style={styles.filterLabel}>Std. Ref</label>
              <Select options={stdRefOptions} value={selectedStdRef} onChange={setSelectedStdRef} isClearable placeholder="Std. Ref" styles={rsFilterSmall} menuPortalTarget={document.body} menuPosition="fixed" />
            </div>
            <div>
              <label style={styles.filterLabel}>Voltage</label>
              <Select options={voltageOptions} value={selectedVoltage} onChange={setSelectedVoltage} isClearable placeholder="Voltage" styles={rsFilterSmall} menuPortalTarget={document.body} menuPosition="fixed" />
            </div>
          </div>

          {/* ── 3rd row: everything else — Test Type, Category, Sub Category, Product Code,
               Description, Batch, From, To — all on one line, same compact size as row 2 ── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(8, minmax(80px, 1fr))", gap: 8, marginTop: 10 }}>

            <div>
              <label style={styles.filterLabel}>Test Type</label>
              <Select options={[]} value={selectedTestType} onChange={setSelectedTestType} placeholder="Select" isClearable styles={rsFilterSmall} />
            </div>

            <div>
              <label style={styles.filterLabel}>Category</label>
              <Select options={categoryOptions} value={selectedCategory} onChange={setSelectedCategory} placeholder="Select" isClearable styles={rsFilterSmall} />
            </div>

            <div>
              <label style={styles.filterLabel}>Sub Category</label>
              <Select options={subCategoryOptions} value={selectedSubCategory} onChange={setSelectedSubCategory} placeholder="Select" isClearable styles={rsFilterSmall} />
            </div>

            <div>
              <label style={styles.filterLabel}>Product Code</label>
              <Select
                options={productCodeOptions}
                value={selectedProductCode}
                onChange={(selected) => {
                  setSelectedProductCode(selected);
                  if (selected) {
                    const parts = selected.label.split(" - ");
                    setDescription(parts.length > 1 ? parts.slice(1).join(" - ") : "");
                  } else {
                    setDescription("");
                  }
                }}
                placeholder="Select"
                isClearable
                styles={rsFilterSmall}
              />
            </div>

            <div>
              <label style={styles.filterLabel}>Description</label>
              <input type="text" className="form-control" placeholder="Search description" value={description} onChange={(e) => setDescription(e.target.value)} style={styles.compactInput} />
            </div>

            <div>
              <label style={styles.filterLabel}>Batch</label>
              <Select options={batchOptions} value={selectedBatch} onChange={setSelectedBatch} placeholder="Select" isClearable styles={rsFilterSmall} />
            </div>

            <div>
              <label style={styles.filterLabel}>From</label>
              <input type="date" className="form-control" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={styles.compactInput} />
            </div>

            <div>
              <label style={styles.filterLabel}>To</label>
              <input type="date" className="form-control" value={toDate} onChange={(e) => setToDate(e.target.value)} style={styles.compactInput} />
            </div>

          </div>
        </div>

        {/* ── Active / Returned Toggle ── */}
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 14 }}>
          <div style={styles.toggleGroup}>
            <button
              style={{ ...styles.toggleBtn, ...(activeTab === "active" ? styles.toggleBtnActive : {}) }}
              onClick={() => setActiveTab("active")}
            >
              Active <span style={styles.toggleBadge}>{activeCount}</span>
            </button>
            <button
              style={{ ...styles.toggleBtn, ...(activeTab === "returned" ? styles.toggleBtnActive : {}) }}
              onClick={() => setActiveTab("returned")}
            >
              Returned <span style={styles.toggleBadge}>{returnedCount}</span>
            </button>
          </div>
        </div>

        {/* ── Return Button ── */}
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 14 }}>
          <button
            className="btn btn-dark btn-sm"
            style={{ fontSize: 13, padding: "6px 18px", opacity: selectedIds.length === 0 ? 0.5 : 1 }}
            onClick={handleReturn}
            disabled={selectedIds.length === 0}
          >
            Return {selectedIds.length > 0 ? `(${selectedIds.length})` : ""}
          </button>
        </div>

        {/* ── Table ── */}
        <div style={styles.tableContainer}>
          <div className="table-responsive">
            <table className="table mb-0" style={styles.erpTable}>
              <thead>
                <tr>
                  <th style={styles.erpTh}><input type="checkbox" checked={allChecked} onChange={toggleAll} /></th>
                  <th style={styles.erpTh}>Product Code</th>
                  <th style={styles.erpTh}>Description</th>
                  <th style={styles.erpTh}>GRN No</th>
                  <th style={styles.erpTh}>GRN Date</th>
                  <th style={styles.erpTh}>Batch</th>
                  <th style={styles.erpTh}>Qty</th>
                  {activeTab === "returned" && <th style={styles.erpTh}></th>}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={activeTab === "returned" ? 8 : 7} style={{ ...styles.erpTd, textAlign: "center", color: "#94a3b8", padding: "32px" }}>
                      Loading...
                    </td>
                  </tr>
                ) : filteredTableData.length === 0 ? (
                  <tr>
                    <td colSpan={activeTab === "returned" ? 8 : 7} style={{ ...styles.erpTd, textAlign: "center", color: "#94a3b8", padding: "32px" }}>
                      No records found.
                    </td>
                  </tr>
                ) : (
                  filteredTableData.map((row) => (
                    <tr key={row.id}>
                      <td style={styles.erpTd}>
                        <input type="checkbox" checked={selectedIds.includes(row.id)} onChange={() => toggleOne(row.id)} />
                      </td>
                      <td style={styles.erpTd}>
                        {row.product_code ? (
                          <Link
                            to={`/cable/product/product-code?view=${encodeURIComponent(row.product_code)}&returnTo=${encodeURIComponent(location.pathname + location.search)}`}
                            style={styles.prLink}
                            onMouseEnter={e => e.currentTarget.style.color = "#1d4ed8"}
                            onMouseLeave={e => e.currentTarget.style.color = "#2563eb"}
                          >
                            {row.product_code}
                          </Link>
                        ) : ""}
                      </td>
                      <td style={styles.erpTd}>{row.description}</td>
                      <td style={styles.erpTd}>
                        {row.grn_number ? (
                          <Link
                            to={`/cable/goods-receipt/goods-receipt?view=${encodeURIComponent(row.grn_number)}&returnTo=${encodeURIComponent(location.pathname + location.search)}`}
                            style={styles.prLink}
                            onMouseEnter={e => e.currentTarget.style.color = "#1d4ed8"}
                            onMouseLeave={e => e.currentTarget.style.color = "#2563eb"}
                          >
                            {row.grn_number}
                          </Link>
                        ) : ""}
                      </td>
                      <td style={styles.erpTd}>{formatDateDMY(row.grn_date)}</td>
                      <td style={styles.erpTd}>{row.batch_number}</td>
                      <td style={styles.erpTd}>{row.qty}</td>
                      {activeTab === "returned" && (
                        <td style={styles.erpTd}>
                          <div style={{ display: "flex", gap: 6 }}>
                            <button
                              className="btn btn-outline-secondary btn-sm"
                              style={{ fontSize: 12, padding: "4px 10px" }}
                              onClick={() => handlePreview(row.batch_number)}
                            >
                              Preview
                            </button>
                            <button
                              className="btn btn-outline-primary btn-sm"
                              style={{ fontSize: 12, padding: "4px 10px" }}
                              onClick={() => handleDownload(row.batch_number)}
                            >
                              Download
                            </button>
                            <button
                              className="btn btn-outline-dark btn-sm"
                              style={{ fontSize: 12, padding: "4px 10px" }}
                              onClick={() => handlePrint(row.batch_number)}
                            >
                              Print
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>

      {/* ── Return Confirmation Modal: one comment box per selected product ── */}
      {showReturnModal && (
        <div style={styles.modalOverlay} onClick={closeReturnModal}>
          <div style={styles.modalBox} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={styles.modalTitle}>Confirm Vendor Return</div>
              <button style={styles.modalCloseBtn} onClick={closeReturnModal} disabled={submitting}>×</button>
            </div>

            <div style={styles.modalBody}>
              {filteredTableData
                .filter((row) => selectedIds.includes(row.id))
                .map((row) => (
                  <div key={row.id} style={styles.modalRow}>
                    <div style={styles.modalRowInfo}>
                      <div style={styles.modalRowTitle}>{row.product_code} — {row.description}</div>
                      <div style={styles.modalRowSub}>Batch: {row.batch_number || "—"} &nbsp;|&nbsp; Qty: {row.qty}</div>
                    </div>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Comments for this product"
                      value={modalComments[row.id] || ""}
                      onChange={(e) => setModalComments(prev => ({ ...prev, [row.id]: e.target.value }))}
                      style={{ fontSize: 13, height: 34 }}
                    />
                  </div>
                ))}
            </div>

            <div style={styles.modalFooter}>
              <button className="btn btn-outline-secondary btn-sm" onClick={closeReturnModal} disabled={submitting}>
                Cancel
              </button>
              <button className="btn btn-dark btn-sm" onClick={confirmReturn} disabled={submitting}>
                {submitting ? "Returning..." : "Confirm Return"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

const styles = {
  pageTitleBar: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 },
  pageTitle: { fontSize: 26, fontWeight: 700, color: "#1e293b", marginBottom: 2 },
  pageSubtitle: { fontSize: 13, color: "#64748b" },
  filterCard: { background: "#ffffff", borderRadius: 8, padding: 20, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", marginBottom: 18 },
  filterLabel: { fontSize: 13, fontWeight: 600, marginBottom: 6, color: "#334155", display: "block" },
  compactInput: { fontSize: "13px", height: "32px", padding: "4px 8px" },
  toggleGroup: { display: "inline-flex", border: "1px solid #e2e8f0", borderRadius: 6, overflow: "hidden", background: "#f8fafc" },
  toggleBtn: { padding: "6px 18px", fontSize: 13, fontWeight: 500, border: "none", background: "transparent", color: "#64748b", cursor: "pointer", display: "flex", alignItems: "center", gap: 6 },
  toggleBtnActive: { background: "#1e293b", color: "#ffffff" },
  toggleBadge: { background: "rgba(255,255,255,0.25)", borderRadius: 10, padding: "1px 7px", fontSize: 11, fontWeight: 700 },
  tableContainer: { background: "#ffffff", borderRadius: 8, padding: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.08)" },
  erpTable: { width: "100%", tableLayout: "auto", borderCollapse: "collapse" },
  erpTh: { background: "#f8fafc", fontSize: 13, fontWeight: 600, padding: "10px 12px", borderBottom: "1px solid #e5e7eb", color: "#1e293b", whiteSpace: "nowrap" },
  erpTd: { fontSize: 13, padding: "10px 12px", borderBottom: "1px solid #f1f5f9", color: "#334155", verticalAlign: "middle" },
  prLink: { fontWeight: 600, fontSize: 13, whiteSpace: "nowrap", color: "#2563eb", textDecoration: "underline", textUnderlineOffset: 3, cursor: "pointer" },

  modalOverlay: { position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10000 },
  modalBox: { background: "#fff", borderRadius: 8, width: "560px", maxWidth: "92vw", maxHeight: "82vh", display: "flex", flexDirection: "column", boxShadow: "0 10px 30px rgba(0,0,0,0.2)" },
  modalHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: "1px solid #e5e7eb" },
  modalTitle: { fontSize: 16, fontWeight: 700, color: "#1e293b" },
  modalCloseBtn: { border: "none", background: "transparent", fontSize: 22, lineHeight: "22px", color: "#64748b", cursor: "pointer" },
  modalBody: { padding: "16px 20px", overflowY: "auto", display: "flex", flexDirection: "column", gap: 14 },
  modalRow: { display: "flex", flexDirection: "column", gap: 6, paddingBottom: 12, borderBottom: "1px solid #f1f5f9" },
  modalRowInfo: { display: "flex", flexDirection: "column", gap: 2 },
  modalRowTitle: { fontSize: 13, fontWeight: 600, color: "#1e293b" },
  modalRowSub: { fontSize: 12, color: "#64748b" },
  modalFooter: { display: "flex", justifyContent: "flex-end", gap: 10, padding: "14px 20px", borderTop: "1px solid #e5e7eb" },
};

export default VendorReturn;